import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';
import TrimNameInput from './TrimNameInput';

// Per Quoting_markdown/shams.md — sham has 4 fixed sizes (not free-typed
// Width/Length like pillow); the user picks a size name and the form fills
// in width_inches/height_inches from this table so the existing generic
// columns stay populated for display (objectSummaryLine, etc.) while
// sham_size itself drives shamCalculations.ts's formulas server-side.
// Welt/Trim/Ruffle/Insert options mirror pillow's UI shape but sham prices
// them from its own dedicated sham_* rates and SHAM_INSERTS table, not
// pillow's.
// Width is the long dimension (matches how a sham actually sits — wider
// than tall), not the SOP's raw listing order — see shamCalculations.ts.
const SHAM_SIZES: Record<string, { width: number; length: number; label: string }> = {
  standard: { width: 26, length: 20, label: 'Standard (26" × 20")' },
  euro:     { width: 28, length: 28, label: 'Euro (28" × 28")' },
  queen:    { width: 30, length: 20, label: 'Queen (30" × 20")' },
  king:     { width: 36, length: 20, label: 'King (36" × 20")' },
};

type ShamSize = 'standard' | 'euro' | 'queen' | 'king' | '';
type WeltType = 'none' | 'self' | 'contrast';
type InsertType = '90_10' | '50_50' | 'poly' | '';

interface State {
  shamSize: ShamSize;
  fabricDescription: string;
  patternRepeatInches: string;
  weltType: WeltType;
  trimOn: boolean;
  trimDescription: string;
  ruffle: boolean;
  insertType: InsertType;
}

const ShamForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [state, setState] = useState<State>({
    shamSize: (iv?.shamSize as ShamSize) || '',
    fabricDescription: iv?.fabricDescription || '',
    patternRepeatInches: iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '',
    weltType: (iv?.weltType as WeltType) || 'none',
    trimOn: !!iv?.trimPlacement && iv.trimPlacement !== 'none',
    trimDescription: iv?.trimDescription || '',
    ruffle: !!iv?.ruffle,
    insertType: (iv?.insertType as InsertType) || '',
  });
  const [error, setError] = useState<string | null>(null);
  const s = (patch: Partial<State>) => setState((prev) => ({ ...prev, ...patch }));

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!state.shamSize) {
        setError('Size is required');
        return null;
      }
      setError(null);
      const { width, length } = SHAM_SIZES[state.shamSize];
      return {
        shamSize: state.shamSize,
        widthInches: width,
        heightInches: length,
        fabricDescription: state.fabricDescription.trim() || null,
        patternRepeatInches: state.patternRepeatInches ? parseFloat(state.patternRepeatInches) : null,
        weltType: state.weltType,
        trimPlacement: state.trimOn ? 'bottom' : 'none',
        trimDescription: state.trimDescription.trim() || null,
        ruffle: state.ruffle,
        insertType: state.insertType || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Size</label>
          <select
            className="input"
            value={state.shamSize}
            onChange={(e) => s({ shamSize: e.target.value as ShamSize })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            {Object.entries(SHAM_SIZES).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Pattern Repeat (inches) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="number" min="0" step="0.25" className="input"
            placeholder="None"
            value={state.patternRepeatInches}
            onChange={(e) => s({ patternRepeatInches: e.target.value })}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={state.fabricDescription}
            onChange={(name) => s({ fabricDescription: name })}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Welt</label>
          <select
            className="input"
            value={state.weltType}
            onChange={(e) => s({ weltType: e.target.value as WeltType })}
            disabled={props.disabled}
          >
            <option value="none">None (Knife Edge)</option>
            <option value="self">Self Welt</option>
            <option value="contrast">Contrast Welt</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Trim</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer', padding: '9px 0' }}>
            <input
              type="checkbox"
              checked={state.trimOn}
              onChange={(e) => s({ trimOn: e.target.checked })}
              disabled={props.disabled}
            />
            Add trim
          </label>
        </div>
        <TrimNameInput value={state.trimDescription} onChange={(v) => s({ trimDescription: v })} disabled={props.disabled} />
        <div className="form-group" style={{ flex: 1 }}>
          <label>Ruffle</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, cursor: 'pointer', padding: '9px 0' }}>
            <input
              type="checkbox"
              checked={state.ruffle}
              onChange={(e) => s({ ruffle: e.target.checked })}
              disabled={props.disabled}
            />
            Add ruffle
          </label>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Insert Type</label>
          <select
            className="input"
            value={state.insertType}
            onChange={(e) => s({ insertType: e.target.value as InsertType })}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="90_10">90/10</option>
            <option value="50_50">50/50</option>
            <option value="poly">Poly</option>
          </select>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default ShamForm;
